import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { GapOperation } from '../permissions/gap-permissions';
import { GapAccessService } from '../services/gap-access/gap-access.service';

/** Why a page was not opened: no access to GAP, access not known (authz did not answer), or too low a level. */
export type GapDenial = 'no-access' | 'unreachable' | 'level';

/** Why a user was refused, by the state of their access. */
const DENIALS: Record<'granted' | 'denied' | 'unreachable', GapDenial> = { granted: 'level', denied: 'no-access', unreachable: 'unreachable' };

/** What the access page is given: why, the page asked for, and the operation it needs, if any. */
export interface GapDeniedState {
  reason: GapDenial;
  returnUrl: string;
  requires?: GapOperation;
}

/**
 * Opens a page only for a user GAP lets in, at the level the page needs: `data: { requires: 'client:POST /v1/clients' }` on its
 * route. Typed URLs too, as every navigation goes through it. Otherwise the user goes to the access page, which says
 * why; the page never shows, only for its calls to be refused.
 */
@Injectable({ providedIn: 'root' })
export class GapPermissionGuard implements CanActivate {
  constructor(
    private readonly _gapAccessService: GapAccessService,
    private readonly _router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.check(state.url, route.data?.['requires']);
  }

  /** Whether the user may open this page, which needs this operation; sends them to the access page otherwise. */
  async check(url: string, requires?: GapOperation): Promise<boolean> {
    const access = await firstValueFrom(this._gapAccessService.current());
    if (access.status === 'granted' && (!requires || this._gapAccessService.can(requires))) {
      return true;
    }
    // An operation whose service did not publish its levels cannot be checked, which is not the user's level
    const unchecked = access.status === 'granted' && !!requires && this._gapAccessService.requiredLevel(requires) === null;
    const reason = unchecked ? 'unreachable' : DENIALS[access.status];
    const denied: GapDeniedState = { reason, returnUrl: url, requires };
    // A page opened from outside GAP (a typed URL, a link) is in the browser's history: replaced, so that going back does
    // not lead to it, only to be refused again. From inside GAP, the refused page never got there: nothing to replace.
    this._router.navigate(['/error'], { state: denied, replaceUrl: !this._router.navigated });
    return false;
  }
}
